// src/pages/HistoryPage.js

import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout"; // Assuming Layout component exists
import "../assets/css/Historypage.css"; // We will create this new CSS file
import axios from "axios";
import FileViewerModal from "../components/FileViewerModal";

// SVG Icons for View and Download actions
const ViewIcon = ({ size = 16 }) => (
  <svg
    style={{ verticalAlign: "middle" }}
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);
const DownloadIcon = ({ size = 16 }) => (
  <svg
    style={{ verticalAlign: "middle" }}
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

// Base URL for the API
const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

// Internal component for rendering a single history item as a card
const HistoryItemCard = ({ item, caseId, onViewFile }) => {

  const [isExpanded, setIsExpanded] = useState(false);
  const description = item.description || "";
  const files = item.files || [];
  const isLongDescription = description.length > 150;
  const toggleExpansion = () => setIsExpanded(!isExpanded);

  // Custom download handler for authenticated downloads
  const handleDownloadFile = async (file) => {
    try {
      const storedUser = localStorage.getItem("user");
      if (!storedUser) throw new Error("User not found. Please log in.");
      const user = JSON.parse(storedUser);
      const token = user?.token;
      if (!token) throw new Error("Authentication token not found.");

      const response = await axios.get(
        `${BASE_URL}/api/cases/${caseId}/files/${file._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download file: " + (err.response?.data?.message || err.message));
    }
  };

  return (
    <div className="history-item-card">
      <div className="history-item-header">
        <h3 className="history-item-title">{item.title}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <span className="history-item-date">
            {new Date(item.date).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
          {item.status && (
            <span className="history-item-status" style={{ fontSize: '0.95em', color: '#007b83', marginTop: '2px' }}>
              <strong>Current Status:</strong> {item.status}
            </span>
          )}
        </div>
      </div>
      <div className="history-item-body">
        <p className="history-item-description">
          {isLongDescription && !isExpanded
            ? `${description.substring(0, 150)}...`
            : description}
        </p>
        {isLongDescription && (
          <button onClick={toggleExpansion} className="read-more-btn">
            {isExpanded ? "Show Less" : "Read More"}
          </button>
        )}
      </div>
      <div className="history-item-files">
        <h4 className="files-label">Attached Files</h4>
        {files.length > 0 ? (
          <ul className="files-list">
            {files.map((file) => (
              <li key={file._id} className="file-item">
                <span className="file-name">{file.originalname}</span>
                <div className="file-actions">
                  <button
                    type="button"
                    className="action-btn view-btn"
                    title="View File"
                    onClick={() => onViewFile({
                      fileUrl: file.url || `${BASE_URL}/api/cases/${caseId}/files/${file._id}/${file.filename}`,
                      fileType: 'image/png', // Hardcoded for testing
                      fileName: file.originalname
                    })}
                  >
                    <ViewIcon />
                  </button>
                  {file.url ? (
                    <a
                      href={file.url}
                      download={file.originalname}
                      className="action-btn download-btn"
                      title="Download File"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <DownloadIcon />
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="action-btn download-btn"
                      title="Download File"
                      onClick={() => handleDownloadFile(file)}
                    >
                      <DownloadIcon />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="no-files-text">No files attached.</p>
        )}
      </div>
    </div>
  );
};

// The Main History Page Component
const HistoryPage = () => {
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { caseId } = useParams(); // Get caseId from URL

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFileUrl, setModalFileUrl] = useState("");
  const [modalFileType, setModalFileType] = useState("");
  const [modalFileName, setModalFileName] = useState("");

  // Store the current blob URL for cleanup
  const [currentBlobUrl, setCurrentBlobUrl] = useState(null);

  const handleViewFile = async ({ fileUrl, fileType, fileName }) => {
    // If it's an image or PDF, fetch as blob and create blob URL
    if (fileType === "application/pdf" || fileType.startsWith("image/")) {
      try {
        const storedUser = localStorage.getItem("user");
        if (!storedUser) throw new Error("User not found. Please log in.");
        const user = JSON.parse(storedUser);
        const token = user?.token;
        if (!token) throw new Error("Authentication token not found.");

        const response = await axios.get(fileUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: fileType });
        const blobUrl = URL.createObjectURL(blob);
        setModalFileUrl(blobUrl);
        setCurrentBlobUrl(blobUrl);
      } catch (err) {
        console.error("Failed to fetch file as blob", err);
        setModalFileUrl("");
      }
    } else {
      setModalFileUrl(fileUrl);
    }
    setModalFileType(fileType);
    setModalFileName(fileName);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    // Cleanup blob URL
    if (currentBlobUrl) {
      URL.revokeObjectURL(currentBlobUrl);
      setCurrentBlobUrl(null);
    }
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [currentBlobUrl]);

  console.log("caseId", caseId)

  useEffect(() => {
    if (!caseId) return;

    const fetchHistory = async () => {
      setLoading(true);
      setError("");
      try {
        const storedUser = localStorage.getItem("user");
        if (!storedUser) throw new Error("User not found. Please log in.");

        const user = JSON.parse(storedUser);
        const token = user?.token;
        if (!token) throw new Error("Authentication token not found.");

        const response = await axios.get(
          `${BASE_URL}/api/cases/${caseId}/history`,
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        console.log("History API Response:", response.data); // Debug log

        if (response.data.status === "success") {
          setHistoryList(response.data.data.history || []);
        } else {
          throw new Error(response.data.message || "Failed to fetch history");
        }
      } catch (err) {
        console.error("History fetch error:", err);
        setError(err.response?.data?.message || err.message || "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [caseId]);

  // Render content based on the state
  const renderContent = () => {
    if (loading)
      return <div className="loading-container">Loading History...</div>;
    if (error) return <div className="error-container">Error: {error}</div>;
    if (historyList.length === 0) {
      return (
        <div className="no-data-container">
          <h3>No History Found</h3>
          <p>There are no history records to display for this case.</p>
        </div>
      );
    }
    return (
      <div className="history-list">
        {historyList.map((item) => (
          <HistoryItemCard key={item._id} item={item} caseId={caseId} onViewFile={handleViewFile} />
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="history-page-container">
        <div className="page-header">
          <h1>Case History</h1>
          <Link to={`/cases/${caseId}`} className="btn btn-secondary">
            &larr; Back to Case Details
          </Link>
        </div>
        <div className="page-content">{renderContent()}</div>
        <FileViewerModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          fileUrl={modalFileUrl}
          fileType={modalFileType}
          fileName={modalFileName}
        />
      </div>
    </Layout>
  );
};

export default HistoryPage;
